import type {
  WorkOrder,
  WorkOrderEvent,
  WorkOrderKind,
  WorkOrderPriority,
  WorkOrderStatus,
} from "../domain/workOrders";
import type { Alert, AlertExplanation } from "../domain/alerts";
import type { Kpi } from "../domain/kpis";
import type { Site } from "../domain/sites";
import { TODAY } from "./clock";

/*
 * Demo data — the fourteen Madinah work orders the airfield application
 * seeds, expressed in the platform's own shape (numeric priority, kind,
 * zone code, WGS-84 point), plus three alerts with full explanations.
 *
 * Everything here is labelled as a sample in the UI. A thin real answer is
 * never topped up with this.
 */

export const SITES: Site[] = [
  {
    id: "site_oema",
    code: "OEMA",
    name: "Prince Mohammad Bin Abdulaziz International Airport, Madinah",
    timezone: "Asia/Riyadh",
    centre: { lon: 39.705, lat: 24.553 },
    assets: 1342,
    counts: { openDefects: 9, openWorkOrders: 11, openAlerts: 3 },
  },
  {
    id: "site_oedf",
    code: "OEDF",
    name: "King Fahd International Airport, Dammam",
    timezone: "Asia/Riyadh",
    centre: { lon: 49.798, lat: 26.471 },
    assets: 0,
    counts: { openDefects: 0, openWorkOrders: 0, openAlerts: 0 },
  },
];

type Seed = [
  assetTag: string,
  zone: string,
  title: string,
  kind: WorkOrderKind,
  priority: WorkOrderPriority,
  status: WorkOrderStatus,
  description: string,
  crew: string,
  lon: number,
  lat: number,
];

const SEED: Seed[] = [
  [
    "S2440",
    "APRON-1",
    "Marking repaint — Stand 40",
    "corrective",
    3,
    "in_progress",
    "Stand markings faded past 50% — lead-in line and stop bar being repainted.",
    "Line crew A",
    39.7112,
    24.5498,
  ],
  [
    "S2447",
    "APRON-1",
    "Marking repaint — Stand 47",
    "preventive",
    4,
    "approved",
    "Stand identification numerals worn on the eastern half.",
    "Line crew A",
    39.7158,
    24.5501,
  ],
  [
    "S1385",
    "APRON-2",
    "Pavement defect — Stand 85",
    "corrective",
    2,
    "dispatched",
    "Spalling around the stand centreline, loose material near the nose wheel path.",
    "Pavements",
    39.7061,
    24.5531,
  ],
  [
    "S2397",
    "TWY-B",
    "Sign damage — TWY B / RWY 17 mandatory sign",
    "corrective",
    2,
    "in_progress",
    "Mandatory sign panel cracked, legend partly illegible at night.",
    "AGL crew",
    39.7008,
    24.5574,
  ],
  [
    "S2403",
    "TWY-B",
    "Light unit failure — holding position B1",
    "corrective",
    1,
    "dispatched",
    "Two consecutive holding position lights out on the taxiway.",
    "AGL crew",
    39.7014,
    24.5568,
  ],
  [
    "S1572",
    "RWY-17-35",
    "Rubber removal — RWY 17 touchdown zone",
    "predictive",
    2,
    "approved",
    "Rubber build-up in the touchdown zone measured above the friction limit.",
    "Pavements",
    39.6969,
    24.5628,
  ],
  [
    "S1573",
    "RWY-17-35",
    "Marking repaint — RWY 35 threshold",
    "preventive",
    3,
    "approved",
    "Threshold bars and designator due for repaint before the season.",
    "Line crew B",
    39.7221,
    24.5306,
  ],
  [
    "S2462",
    "NAV",
    "Light unit failure — LLZ far-field monitor",
    "corrective",
    2,
    "completed",
    "Localiser far-field monitor obstruction light replaced.",
    "AGL crew",
    39.6934,
    24.5681,
  ],
  [
    "S1356",
    "NAV",
    "Cable / circuit fault — monitor feed",
    "corrective",
    3,
    "draft",
    "Intermittent circuit fault reported on the monitor feed.",
    "Electrical",
    39.6937,
    24.5677,
  ],
  [
    "S2452",
    "APRON-R",
    "Pavement defect — remote apron slabs 14–16",
    "corrective",
    3,
    "on_hold",
    "Joint sealant failure across three slabs on the remote apron. On hold until sealant delivery.",
    "Pavements",
    39.7196,
    24.5457,
  ],
  [
    "S1574",
    "HELIPAD",
    "Marking repaint — Helipad H",
    "preventive",
    4,
    "approved",
    "Helipad H marking and perimeter line faded.",
    "Line crew B",
    39.7238,
    24.5413,
  ],
  [
    "S2825",
    "TWY-A",
    "FOD / debris — holding position A2",
    "corrective",
    1,
    "in_progress",
    "Loose aggregate at the holding position after adjacent works.",
    "Ops response",
    39.7058,
    24.5562,
  ],
  [
    "S1358",
    "NAV",
    "Annual inspection — VOR compound fence",
    "inspection",
    4,
    "verified",
    "Annual inspection of the VOR compound fence and access gate.",
    "Facilities",
    39.7282,
    24.5372,
  ],
  [
    "S2287",
    "MET",
    "Light unit failure — anemometer mast obstruction light",
    "corrective",
    3,
    "approved",
    "Obstruction light on the anemometer mast reported unlit.",
    "AGL crew",
    39.6978,
    24.5535,
  ],
  [
    "F01",
    "TERMINAL",
    "Escalator stopped — arrivals hall, unit 3",
    "corrective",
    2,
    "in_progress",
    "Escalator tripped on the step-chain safety switch. Barriers in place; passengers using the lifts.",
    "Lifts and escalators",
    39.7182,
    24.5531,
  ],
  [
    "F01",
    "TERMINAL",
    "Chiller fault — departures hall too warm",
    "corrective",
    2,
    "dispatched",
    "Chiller 2 alarmed on high head pressure. Departures hall at 27 °C.",
    "HVAC",
    39.7185,
    24.5536,
  ],
  [
    "F01",
    "TERMINAL",
    "Water leak — arrivals toilets, block C",
    "corrective",
    3,
    "approved",
    "Leak under the wash basins; floor wet, two cubicles closed.",
    "Plumbing",
    39.7179,
    24.5528,
  ],
  [
    "F01",
    "TERMINAL",
    "Deep clean — gates 4 to 8 after the Hajj peak",
    "preventive",
    4,
    "approved",
    "Scheduled deep clean of seating, carpets and glass at gates 4–8.",
    "Cleaning crew 2",
    39.7188,
    24.5541,
  ],
  [
    "F25",
    "LANDSIDE",
    "Barrier arm broken — short-stay car park exit",
    "corrective",
    2,
    "dispatched",
    "Exit barrier 2 arm snapped; exit lane running on manual.",
    "Car park team",
    39.7231,
    24.5522,
  ],
  [
    "F25",
    "LANDSIDE",
    "Street lights out — airport entrance road",
    "corrective",
    3,
    "draft",
    "Six lights out between the main roundabout and the drop-off.",
    "Electrical",
    39.7262,
    24.5518,
  ],
  [
    "F09",
    "LANDSIDE",
    "Irrigation leak — entrance landscape",
    "corrective",
    4,
    "approved",
    "Burst drip line at the gateway planting beds; water pooling on the walkway.",
    "Landscape crew",
    39.7248,
    24.5527,
  ],
  [
    "F16",
    "LANDSIDE",
    "Waste collection missed — landside compound",
    "corrective",
    3,
    "completed",
    "Two days of general waste not collected; skips full.",
    "Waste team",
    39.7215,
    24.5498,
  ],
];

/**
 * Where each sample order is, its trade, and its contractor (null = own team),
 * in SEED order. Contractor names are generic on purpose: no operator's real
 * providers are in the sample.
 */
const CLASS: [area: string, trade: string, contractor: string | null][] = [
  ["airside", "civil", "Airfield maintenance contractor"],
  ["airside", "civil", "Airfield maintenance contractor"],
  ["airside", "civil", "Airfield maintenance contractor"],
  ["airside", "airfield_systems", "AGL maintenance contractor"],
  ["airside", "airfield_systems", "AGL maintenance contractor"],
  ["airside", "civil", "Airfield maintenance contractor"],
  ["airside", "civil", "Airfield maintenance contractor"],
  ["airside", "airfield_systems", "AGL maintenance contractor"],
  ["airside", "electrical", "AGL maintenance contractor"],
  ["airside", "civil", "Airfield maintenance contractor"],
  ["airside", "civil", "Airfield maintenance contractor"],
  ["airside", "other", null],
  ["airside", "civil", null],
  ["airside", "airfield_systems", "AGL maintenance contractor"],
  ["terminal", "electromech", "Terminal facilities contractor"],
  ["terminal", "electromech", "Terminal facilities contractor"],
  ["terminal", "utilities", "Terminal facilities contractor"],
  ["terminal", "cleaning", "Terminal cleaning"],
  ["landside", "electromech", "Landside services contractor"],
  ["landside", "electrical", "Landside services contractor"],
  ["landside", "landscape", "Landscape contractor"],
  ["landside", "environmental", "Environmental services contractor"],
];

/** The defects each sample order closes — the same ids the sample inspections raise. */
const DEFECTS_BY_ASSET: Record<string, string[]> = {
  S2403: ["def-agl-b1-03", "def-agl-b1-04"],
  S2825: ["def-fod-a2"],
  S2452: ["def-apron-r-sealant"],
  S1572: ["def-rubber-tdz17"],
};

function iso(d: Date): string {
  return d.toISOString();
}
function daysAgo(n: number, hour = 7): Date {
  const d = new Date(TODAY);
  d.setUTCDate(d.getUTCDate() - n);
  d.setUTCHours(hour, 0, 0, 0);
  return d;
}
function daysAhead(n: number): Date {
  return daysAgo(-n);
}

/**
 * The service level a job carries, by priority. PROPOSED, like every other
 * threshold on this platform — no operator has agreed these hours.
 *
 * The sample carried none at all, which made the SLA column empty on every
 * screen that has one and made the parts hold impossible to demonstrate: a
 * job whose clock is not running cannot have time taken off it. These are
 * the response times an aerodrome maintenance contract usually states, and
 * they are here so the measure can be seen working, not because they are
 * anybody's policy.
 */
const SLA_HOURS_BY_PRIORITY: Record<number, number> = { 1: 4, 2: 8, 3: 24, 4: 72, 5: 168 };

export function seedWorkOrders(): WorkOrder[] {
  return SEED.map((s, i) => {
    const opened = daysAgo(3 + i * 2);
    const done = s[5] === "completed" || s[5] === "verified";
    const completed = done ? daysAgo(Math.max(0, 3 + i * 2 - 4 - (i % 5))) : undefined;
    const started = ["in_progress", "completed", "verified", "on_hold"].includes(s[5])
      ? daysAgo(Math.max(0, 3 + i * 2 - 2))
      : undefined;
    return {
      id: `00000000-0000-4000-8000-${String(i + 1).padStart(12, "0")}`,
      number: `WO-${String(i + 1).padStart(6, "0")}`,
      siteId: "site_oema",
      title: s[2],
      kind: s[3],
      priority: s[4],
      status: s[5],
      description: s[6],
      zoneCode: s[1],
      assetTag: s[0],
      crew: s[7],
      point: { lon: s[8], lat: s[9] },
      openedAt: iso(opened),
      slaHours: SLA_HOURS_BY_PRIORITY[s[4] as number],
      dueAt: s[4] === 1 ? iso(daysAgo(2 + i * 2, 19)) : undefined,
      startedAt: started ? iso(started) : undefined,
      completedAt: completed ? iso(completed) : undefined,
      verifiedAt: s[5] === "verified" ? iso(daysAgo(1)) : undefined,
      alertId: s[3] === "predictive" ? "al-friction-17" : undefined,
      defectIds: DEFECTS_BY_ASSET[s[0]] ?? [],
      capex: false,
      attrs: classOf(i),
    };
  });
}

function classOf(i: number): Record<string, string> {
  const c = CLASS[i];
  if (!c) return {};
  return c[2] ? { area: c[0], trade: c[1], contractor: c[2] } : { area: c[0], trade: c[1] };
}

/** A plausible history for each seeded order, derived from its current state. */
export function seedHistory(wo: WorkOrder): WorkOrderEvent[] {
  const path: WorkOrderStatus[] = ["draft", "approved", "dispatched", "in_progress", "completed", "verified"];
  const idx = path.indexOf(wo.status);
  const stops: WorkOrderStatus[] = idx >= 0 ? path.slice(0, idx + 1) : ["draft", "approved", "dispatched", wo.status];
  const actors = [
    "Duty engineer",
    "Maintenance supervisor",
    "Dispatcher",
    wo.crew ?? "Crew",
    wo.crew ?? "Crew",
    "Quality inspector",
  ];
  const start = new Date(wo.openedAt).getTime();
  const end = (wo.completedAt ? new Date(wo.completedAt) : TODAY).getTime();
  const step = stops.length > 1 ? (end - start) / (stops.length - 1) : 0;
  return stops.map((to, i) => ({
    id: `${wo.id}-ev${i + 1}`,
    workOrderId: wo.id,
    at: new Date(start + step * i).toISOString(),
    by: actors[Math.min(i, actors.length - 1)],
    from: i === 0 ? null : stops[i - 1],
    to,
    note: i === 0 ? "Raised from inspection finding" : undefined,
  }));
}

/* ---- alerts ------------------------------------------------------------------ */

export const ALERTS: AlertExplanation[] = [
  {
    id: "al-friction-17",
    title: "Runway friction below minimum — RWY 17/35, CH 1200–1500",
    kind: "reactive",
    severity: 5,
    status: "open",
    raisedAt: iso(daysAgo(6, 8)),
    ruleKey: "friction_below_minimum",
    ruleName: "Minimum friction level",
    ruleClass: "regulatory",
    assetTag: "RWY-17-35",
    zoneCode: "RWY-17-35",
    point: { lon: 39.6969, lat: 24.5628 },
    subjectKind: "segment",
    subjectLabel: "RWY 17/35 · CH 1200–1500 · lane +3 m",
    occurrences: 3,
    expression: "segment_avg(friction_mu, window=3) < params.min_friction",
    reference: "GACAR Part 139 §139.327; ICAO Annex 14 Vol I, Table A-1 (Attachment A)",
    parameters: { min_friction: 0.43, window: 3, device: "ASFT", speed_kmh: 65 },
    resolved: {
      "segment_avg(friction_mu, window=3)": 0.41,
      segments: [
        { chainage: "1200–1300", mu: 0.42 },
        { chainage: "1300–1400", mu: 0.4 },
        { chainage: "1400–1500", mu: 0.41 },
      ],
      survey: "FR-2026-07-11",
    },
    verifyRequired: true,
    outcome: "fired",
    resultingActions: [
      { kind: "work_order", ref: "WO-000006", title: "Rubber removal — RWY 17 touchdown zone", status: "approved" },
    ],
  },
  {
    id: "al-agl-b1",
    title: "Two consecutive holding-position lights unserviceable — B1",
    kind: "reactive",
    severity: 4,
    status: "open",
    raisedAt: iso(daysAgo(11, 21)),
    ruleKey: "agl_consecutive_outage",
    ruleName: "Consecutive AGL unit outage",
    ruleClass: "safety",
    assetTag: "HP-B1-03",
    zoneCode: "TWY-B",
    point: { lon: 39.7014, lat: 24.5568 },
    subjectKind: "asset",
    subjectLabel: "Holding position B1, units 3–4",
    occurrences: 1,
    expression: "consecutive_out(agl_units, min_run=params.min_run) >= params.min_run",
    reference: "ICAO Annex 14 Vol I, 10.5.7; Aerodrome Manual Part E §4.2",
    parameters: { min_run: 2, circuit: "HP-B" },
    resolved: {
      "consecutive_out(agl_units, min_run=2)": 2,
      units: ["HP-B1-03", "HP-B1-04"],
      last_serviceable_check: iso(daysAgo(12)),
    },
    verifyRequired: false,
    outcome: "fired",
    resultingActions: [
      { kind: "work_order", ref: "WO-000005", title: "Light unit failure — holding position B1", status: "dispatched" },
    ],
  },
  {
    id: "al-lamp-life",
    title: "Six runway edge lamps past 90% of rated life",
    kind: "predictive",
    severity: 3,
    status: "open",
    raisedAt: iso(daysAgo(2, 4)),
    predictedFor: iso(daysAhead(19)),
    confidence: 0.82,
    ruleKey: "lamp_end_of_life",
    ruleName: "Lamp end-of-life prediction",
    ruleClass: "predictive",
    assetTag: "RWY-17-35",
    zoneCode: "RWY-17-35",
    point: { lon: 39.7015, lat: 24.5455 },
    subjectKind: "asset",
    subjectLabel: "RWY 17/35 edge lights, circuit E-2",
    occurrences: 1,
    expression: "hours_run / rated_hours >= params.life_fraction",
    reference: "Manufacturer rated life 1,000 h (halogen 200 W); Aerodrome Manual Part E §4.5",
    parameters: { life_fraction: 0.9, rated_hours: 1000 },
    resolved: {
      "hours_run / rated_hours": [0.93, 0.92, 0.91, 0.91, 0.9, 0.9],
      units: ["E2-011", "E2-012", "E2-014", "E2-019", "E2-020", "E2-023"],
      predicted_failure_window_days: 19,
    },
    verifyRequired: false,
    outcome: "fired",
    confirmed: null,
    resultingActions: [],
  },
];

export function seedAlerts(siteId: string): Alert[] {
  if (siteId !== "site_oema") return [];
  return ALERTS.map(({ resultingActions: _r, parameters: _p, resolved: _v, ...a }) => a);
}

/* ---- KPIs ---------------------------------------------------------------------- */

export function seedKpis(siteId: string): Kpi[] {
  if (siteId !== "site_oema") {
    return BASE_KPIS.map((k) => ({
      ...k,
      value: null,
      reason: "No feeds connected for this site yet",
      history: undefined,
    }));
  }
  return BASE_KPIS;
}

const BASE_KPIS: Kpi[] = [
  {
    key: "punctuality",
    label: "On-time departures within 15 min",
    unit: "%",
    direction: "up",
    target: 85,
    value: 88.2,
    source: "AODB departure times",
    history: [84, 86, 83, 87, 89, 88, 88.2],
  },
  {
    key: "safety",
    label: "Serious safety incidents open",
    unit: "open",
    direction: "down",
    target: 0,
    value: 0,
    source: "incident register, serious and open",
    history: [1, 1, 0, 0, 0, 0, 0],
  },
  {
    key: "security",
    label: "Security incidents and breaches open",
    unit: "open",
    direction: "down",
    target: 0,
    value: 1,
    source: "incident register, access control and the security posting",
    history: [0, 0, 1, 1, 1, 1, 1],
  },
  {
    key: "pax",
    label: "Passengers against plan",
    unit: "% plan",
    direction: "up",
    target: 100,
    value: 96.4,
    source: "passenger feed against the annual plan",
    history: [92, 94, 95, 97, 96, 96, 96.4],
  },
  {
    key: "queue",
    label: "Worst passenger queue",
    unit: "min",
    direction: "down",
    target: 15,
    value: 11,
    source: "queue counters and the checkpoint feed",
    history: [18, 14, 12, 16, 13, 12, 11],
  },
  {
    key: "baggage",
    label: "Bags delivered on time",
    unit: "%",
    direction: "up",
    target: 97,
    value: 95.1,
    source: "baggage handling feed",
    history: [96, 97, 95, 94, 96, 95, 95.1],
  },
  {
    key: "runway_availability_pct",
    label: "Runway availability",
    unit: "%",
    direction: "up",
    target: 99.5,
    value: null,
    reason: "'period_minutes' is not available: the core does not yet model operational periods",
    source: "closure NOTAMs against 24 hours",
  },
  {
    key: "outages",
    label: "Major equipment out of service",
    unit: "units",
    direction: "down",
    target: 2,
    value: 3,
    source: "lifts, bridges, baggage lines and screening lanes",
    history: [2, 2, 4, 3, 3, 3, 3],
  },
  {
    key: "weather",
    label: "Weather disruption today",
    unit: "min",
    direction: "down",
    target: 60,
    value: 0,
    source: "disruption feed, or inferred from the weather when it is not fed",
    history: [0, 45, 0, 0, 0, 0, 0],
  },
  {
    key: "revenue",
    label: "Revenue against target",
    unit: "% plan",
    direction: "up",
    target: 100,
    value: 101.7,
    source: "finance feed",
    history: [98, 99, 100, 101, 102, 101, 101.7],
  },
  {
    key: "predictive_precision_pct",
    label: "Predictive alert precision",
    unit: "%",
    direction: "up",
    target: 80,
    value: 83,
    source: "confirmed vs refuted predictive alerts",
    history: [70, 75, 78, 80, 81, 83, 83],
  },
  {
    key: "mttr_p1_hours",
    label: "Mean time to repair, P1",
    unit: "h",
    direction: "down",
    target: 24,
    value: 19.5,
    source: "work-order events",
    history: [30, 26, 22, 21, 20, 19, 19.5],
  },
];
